import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AlertService, AlertType } from '../../services/alert.service';

export interface EmpresaLogoDialogData {
    urlLogoAtual: string | null;
}

export interface EmpresaLogoDialogResult {
    remover: boolean;
    arquivo: File | null;
    previewUrl: string | null;
}

const TAMANHO_MAX_BYTES = 2 * 1024 * 1024;
const EXTENSOES_PERMITIDAS = ['webp', 'jpg', 'jpeg', 'png', 'svg'];

@Component({
    selector: 'app-empresa-logo-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule],
    templateUrl: './empresa-logo-dialog.component.html',
    styleUrls: ['./empresa-logo-dialog.component.css'],
})
export class EmpresaLogoDialogComponent implements OnDestroy {
    public urlExibida: string | null;
    public arquivoSelecionado: File | null = null;
    public marcadoParaRemover: boolean = false;
    private previewLocalUrl: string | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: EmpresaLogoDialogData,
        private dialogRef: MatDialogRef<EmpresaLogoDialogComponent, EmpresaLogoDialogResult>,
        private alertService: AlertService
    ) {
        this.urlExibida = data?.urlLogoAtual ?? null;
    }

    onArquivoSelecionado(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files[0];
        if (!file) return;

        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!EXTENSOES_PERMITIDAS.includes(ext)) {
            this.alertService.show(
                'Atenção',
                `Formato não permitido. Aceitos: ${EXTENSOES_PERMITIDAS.join(', ')}.`,
                AlertType.warning
            );
            input.value = '';
            return;
        }

        if (file.size > TAMANHO_MAX_BYTES) {
            this.alertService.show('Atenção', 'O arquivo excede 2 MB.', AlertType.warning);
            input.value = '';
            return;
        }

        this.descartarPreviewLocal();
        this.arquivoSelecionado = file;
        this.marcadoParaRemover = false;
        this.previewLocalUrl = URL.createObjectURL(file);
        this.urlExibida = this.previewLocalUrl;
    }

    marcarParaRemover(): void {
        this.descartarPreviewLocal();
        this.arquivoSelecionado = null;
        this.marcadoParaRemover = true;
        this.urlExibida = null;
    }

    desfazerRemocao(): void {
        this.marcadoParaRemover = false;
        this.urlExibida = this.data?.urlLogoAtual ?? null;
    }

    cancelar(): void {
        this.descartarPreviewLocal();
        this.dialogRef.close();
    }

    confirmar(): void {
        // Transfere a posse da blob URL pro componente pai. Ao zerar `previewLocalUrl`
        // antes do ngOnDestroy, evitamos que o `revokeObjectURL` quebre a imagem fora do dialog.
        const previewUrl = this.previewLocalUrl;
        this.previewLocalUrl = null;
        this.dialogRef.close({
            remover: this.marcadoParaRemover,
            arquivo: this.arquivoSelecionado,
            previewUrl,
        });
    }

    private descartarPreviewLocal(): void {
        if (this.previewLocalUrl) {
            URL.revokeObjectURL(this.previewLocalUrl);
            this.previewLocalUrl = null;
        }
    }

    ngOnDestroy(): void {
        this.descartarPreviewLocal();
    }
}
